import React from 'react';
import { Button, IconButton, Tooltip, MenuItem, Select, FormControl, Checkbox, ListItemText } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import SendIcon from '@mui/icons-material/Send';
import { Close } from '@mui/icons-material';
import { FaSteam } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import ExtensionStatus from '../../../extension/ExtensionStatus';
import { ViewMode } from '../../../../types/trades';

interface TradeHistoryFilterBarProps {
    mode: ViewMode;
    searchQuery: string;
    statusFilter: string[];
    isGrouped: boolean;
    loading: boolean;
    counts: { sold: number; purchased: number };
    onViewModeChange: (mode: ViewMode) => void;
    onSearchChange: (value: string) => void;
    onStatusFilterChange: (value: string[]) => void;
    onToggleGrouped: () => void;
    onAcceptAllSteamOffers: () => void;
    onAcceptAllTrades: () => void;
    onSendAll: () => void;
    onCancelAll: () => void;
    hasTradesToAccept: boolean;
    hasTradesToSend: boolean;
    hasTradesToCancel: boolean;
    hasSteamOffersToAccept: boolean;
}

const TradeHistoryFilterBar: React.FC<TradeHistoryFilterBarProps> = ({ mode, searchQuery, statusFilter, isGrouped, loading, counts, onViewModeChange, onSearchChange, onStatusFilterChange, onToggleGrouped, onAcceptAllSteamOffers, onAcceptAllTrades, onSendAll, onCancelAll, hasTradesToAccept, hasTradesToSend, hasTradesToCancel, hasSteamOffersToAccept }) => {
    const { t } = useTranslation();

    const filterOptions = React.useMemo(() => {
        const baseOptions = [{ value: 'ALL', label: t('history.filter.all') || 'All' }];

        if (mode === 'sold') {
            return [
                ...baseOptions,
                { value: 'WAITING_FOR_SELLER_CONFIRM', label: t('history.status.waiting_seller_confirm') || 'Waiting Confirm' },
                { value: 'WAITING_FOR_SELLER_SEND', label: t('history.status.waiting_seller_send') || 'Waiting Send' },
                { value: 'WAITING_FOR_BUYER_ACCEPT', label: t('history.status.waiting_buyer_accept') || 'Dispatched' },
                { value: 'COMPLETED', label: t('history.status.completed') || 'Completed' },
                { value: 'WAITING_FOR_SELLER_CONFIRM_CANCELLED', label: t('history.status.confirm_cancelled') || 'Cancelled (No Show)' },
                { value: 'WAITING_FOR_SELLER_SEND_CANCELLED', label: t('history.status.send_cancelled') || 'Cancelled (Not Sent)' }
            ];
        } else {
            return [
                ...baseOptions,
                { value: 'WAITING_FOR_SELLER_CONFIRM', label: t('history.status.waiting_seller_confirm') || 'Awaiting Seller' },
                { value: 'WAITING_FOR_BUYER_ACCEPT', label: t('history.status.waiting_buyer_accept') || 'Ready to Accept' },
                { value: 'COMPLETED', label: t('history.status.completed') || 'Completed' },
                { value: 'COMPLETED_CANCELLED', label: t('history.status.completed_cancelled') || 'Cancelled' }
            ];
        }
    }, [mode, t]);

    const renderStatusValue = (selected: string[]) => {
        if (selected.includes('ALL') || selected.length === 0) {
            return t('history.filter.all') || 'All';
        }
        if (selected.length === 1) {
            const opt = filterOptions.find(o => o.value === selected[0]);
            return opt ? opt.label : selected[0];
        }
        return `${selected.length} selected`;
    };

    const customMenuProps = {
        disableScrollLock: true,
        elevation: 0,
        anchorOrigin: {
            vertical: 'bottom' as const,
            horizontal: 'left' as const,
        },
        transformOrigin: {
            vertical: 'top' as const,
            horizontal: 'left' as const,
        },
        PaperProps: {
            sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                border: '1px solid #404040',
                borderRadius: '8px',
                mt: 1.5,
                bgcolor: 'background.paper',
                '& .MuiMenuItem-root': {
                    py: 1,
                    px: 2,
                    fontWeight: 500,
                },
                '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    left: 24,
                    width: 12,
                    height: 12,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderLeft: '1px solid #404040',
                    borderTop: '1px solid #404040',
                },
            },
        },
    };

    return (
        <div className="flex flex-wrap 2xl:flex-nowrap justify-between items-center gap-3 w-full">
            {/* Left Group: Tabs, Search, Status Filter */}
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 w-full 2xl:w-auto">
                <div className="flex grow sm:grow-0 min-w-[260px] p-1 bg-gray-50 dark:bg-neutral-900/50 rounded-xl border border-gray-100 dark:border-white/5 h-11 items-center shadow-inner">
                    {(['sold', 'purchased'] as ViewMode[]).map((v) => {
                        const count = v === 'sold' ? counts.sold : counts.purchased;
                        return (
                            <button
                                key={v}
                                onClick={() => onViewModeChange(v)}
                                className={`relative flex-1 px-4 h-full rounded-lg text-xs font-black uppercase tracking-wider transition-all duration-300 ${mode === v ? 'bg-white dark:bg-dark-primary text-blue-600 dark:text-white shadow-sm z-10' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-0'}`}
                            >
                                {t(`history.tabs.${v}`)}
                                {count > 0 && (
                                    <span className="absolute top-0.5 right-0.5 flex h-5 min-w-[20px] px-1 items-center justify-center rounded-full bg-red-500 font-bold text-white shadow-sm z-20">
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="relative grow sm:grow-0 min-w-[150px] sm:w-[200px] h-11 group">
                    <SearchIcon
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors"
                        sx={{ fontSize: 18 }}
                    />
                    <input
                        type="text"
                        placeholder={t('history.search_placeholder') || 'Search ID...'}
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        className="w-full h-full bg-gray-50 dark:bg-neutral-800/50 border border-gray-100 dark:border-white/5 rounded-xl py-3 pl-11 pr-4 text-xs text-gray-700 dark:text-gray-200 focus:outline-none focus:border-blue-500/50 dark:focus:border-dark-primary transition-all shadow-inner"
                    />
                </div>

                <FormControl size="small" className="grow sm:grow-0 min-w-[190px] sm:w-[220px] h-11">
                    <Select
                        multiple
                        value={statusFilter}
                        onChange={(e) => {
                            const value = e.target.value;
                            onStatusFilterChange(typeof value === 'string' ? value.split(',') : value);
                        }}
                        renderValue={renderStatusValue}
                        MenuProps={customMenuProps}
                        className="w-full h-full bg-gray-50 dark:bg-neutral-800/50 border border-gray-100 dark:border-white/5 text-xs text-gray-700 dark:text-gray-200 shadow-inner font-semibold uppercase tracking-wider"
                        sx={{
                            borderRadius: '0.75rem',
                            overflow: 'hidden',
                            boxShadow: 'none',
                            '.MuiOutlinedInput-notchedOutline': { border: 0 },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
                            '.MuiSelect-select': {
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                                boxSizing: 'border-box',
                                py: 0,
                                pl: '16px',
                                pr: '32px',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                            }
                        }}
                    >
                        {filterOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value} className="!text-xs !font-medium !uppercase !tracking-wider">
                                <Checkbox checked={statusFilter.indexOf(opt.value) > -1} size="small" className="!p-1 !mr-2" />
                                <ListItemText primary={opt.label} classes={{ primary: '!text-xs !font-medium !uppercase !tracking-wider' }} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            {/* Right Group: Actions, Grouped Toggle, Extension Status */}
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 w-full 2xl:w-auto">
                <div className="flex grow sm:grow-0 items-center h-11 bg-gray-50 dark:bg-neutral-900/50 border border-gray-100 dark:border-white/5 rounded-xl overflow-hidden shadow-inner divide-x divide-gray-100 dark:divide-white/5">
                    {mode === 'purchased' && (
                        <>
                            <Button
                                size="small"
                                onClick={onAcceptAllSteamOffers}
                                disabled={loading || !hasSteamOffersToAccept}
                                className="flex-1 sm:flex-initial hover:!bg-white/50 dark:hover:!bg-neutral-800/50 !font-semibold !h-full !px-5 !text-xs whitespace-nowrap"
                                sx={{ minWidth: 0, borderRadius: 0, padding: 0, '&.Mui-disabled': { pointerEvents: 'auto !important', cursor: 'not-allowed !important' } }}
                            >
                                <FaSteam className="w-4 h-4 mr-2" />
                                Accept All
                            </Button>

                            <Tooltip title={t('history.tooltips.seller_penalty_info') || 'Penalty risk on cancel'} arrow>
                                <span className="h-full">
                                    <IconButton
                                        onClick={onCancelAll}
                                        disabled={loading || !hasTradesToCancel}
                                        className="!text-red-500 hover:!bg-red-500/10 dark:hover:!bg-red-500/20 !h-full !w-11"
                                        size="small"
                                        sx={{ p: 0, borderRadius: 0, borderTopRightRadius: '0.75rem', borderBottomRightRadius: '0.75rem', '&.Mui-disabled': { pointerEvents: 'auto !important', cursor: 'not-allowed !important' } }}
                                    >
                                        <Close fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </>
                    )}

                    {mode === 'sold' && (
                        <>
                            <Button
                                size="small"
                                onClick={onAcceptAllTrades}
                                disabled={loading || !hasTradesToAccept}
                                className="flex-1 sm:flex-initial hover:!bg-white/50 dark:hover:!bg-neutral-800/50 !font-semibold !h-full !px-5 !text-xs whitespace-nowrap"
                                sx={{ minWidth: 0, borderRadius: 0, padding: 0, '&.Mui-disabled': { pointerEvents: 'auto !important', cursor: 'not-allowed !important' } }}
                            >
                                <DoneAllIcon className="mr-2" sx={{ fontSize: 16 }} />
                                Accept All
                            </Button>

                            <Button
                                size="small"
                                onClick={onSendAll}
                                disabled={loading || !hasTradesToSend}
                                className="flex-1 sm:flex-initial hover:!bg-white/50 dark:hover:!bg-neutral-800/50 !font-semibold !h-full !px-5 !text-xs whitespace-nowrap"
                                sx={{ minWidth: 0, borderRadius: 0, padding: 0, '&.Mui-disabled': { pointerEvents: 'auto !important', cursor: 'not-allowed !important' } }}
                            >
                                <SendIcon className="mr-2" sx={{ fontSize: 14 }} />
                                Send All
                            </Button>

                            <Tooltip title={t('history.tooltips.seller_penalty_info') || 'Penalty risk on cancel'} arrow>
                                <span className="h-full">
                                    <IconButton
                                        onClick={onCancelAll}
                                        disabled={loading || !hasTradesToCancel}
                                        className="!text-red-500 hover:!bg-red-500/10 dark:hover:!bg-red-500/20 !h-full !w-11"
                                        size="small"
                                        sx={{ p: 0, borderRadius: 0, borderTopRightRadius: '0.75rem', borderBottomRightRadius: '0.75rem', '&.Mui-disabled': { pointerEvents: 'auto !important', cursor: 'not-allowed !important' } }}
                                    >
                                        <Close fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </>
                    )}
                </div>

                <div className="flex items-center gap-3 grow sm:grow-0">
                    <div className="flex grow sm:grow-0 min-w-[150px] sm:w-[150px] max-w-[240px] justify-between items-center md:gap-1 gap-2 bg-gray-50 dark:bg-neutral-900/50 md:px-4 px-2 h-11 rounded-xl border border-gray-100 dark:border-white/5 shadow-inner">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Grouped</span>
                        <button
                            onClick={onToggleGrouped}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 focus:outline-none ${isGrouped ? 'bg-blue-500 dark:bg-dark-primary' : 'bg-gray-200 dark:bg-neutral-700'}`}
                        >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 ${isGrouped ? 'translate-x-4' : 'translate-x-1'}`} />
                        </button>
                    </div>

                    <div className="flex shrink-0 items-center h-11 [&_*]:text-xs [&_.extension-full-text]:hidden sm:[&_.extension-full-text]:inline">
                        <ExtensionStatus />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TradeHistoryFilterBar;